import { logger } from '@/utils/logger';
import { useAudioPlayerStore } from '@/stores/audioPlayerStore';
import { useTerms, getTermByCategory } from '@/queries/useTermQueries';
import { SectionTitle, SubTitle } from '@/styles/Typography';
import { Term } from '@/types/term_type';
import { sanitizeURL } from '@/utils/xssProtection';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { styled, useTheme } from 'styled-components';

// footerBg를 동적으로 import
const loadFooterBg = () => import('@assets/footer.webp');

export const Footer: React.FC = () => {
  const location = useLocation();
  const isMain = location.pathname === '/';
  const theme = useTheme();
  const { currentTrack } = useAudioPlayerStore();
  const isPlayerVisible = !!currentTrack;
  const { data: terms } = useTerms();
  const privacyTerm = getTermByCategory(terms, 'PRIVACY');
  const serviceTerm = getTermByCategory(terms, 'TERMS');

  // 이미지 lazy loading 상태 관리
  const [footerBgUrl, setFooterBgUrl] = useState<string>('');
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const footerRef = useRef<HTMLElement>(null);

  const handleTermClick = (term: Term | undefined) => {
    if (!term) {
      logger.error('Term data not loaded');
      return;
    }

    // Sanitize URL before opening to prevent javascript: or data: protocol attacks
    const sanitizedUrl = sanitizeURL(term.content_url);
    if (!sanitizedUrl) {
      logger.error('Invalid URL');
      return;
    }

    window.open(sanitizedUrl, '_blank');
  };

  useEffect(() => {
    if (!isMain) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // 뷰포트에 들어왔을 때 이미지 로드
            loadFooterBg().then(module => {
              setFooterBgUrl(module.default);
              // 이미지 프리로드
              const img = new Image();
              img.src = module.default;
              img.onload = () => setIsImageLoaded(true);
            });
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // 100px 전에 미리 로드 시작
      },
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => observer.disconnect();
  }, [isMain]);

  return (
    <>
      <FooterContainer
        ref={footerRef}
        $isMain={isMain}
        $bgUrl={footerBgUrl}
        $isLoaded={isImageLoaded}
        $playerPB={isPlayerVisible ? theme.playerHeight : 0}
      >
        {/* 로딩 중 placeholder */}
        {isMain && !isImageLoaded && <LoadingPlaceholder />}

        {/* 메인 페이지용 푸터 */}
        {isMain && (
          <MainFooterContainer $isVisible={isImageLoaded}>
            <h1>Ready to Own Your Music?</h1>
            <p>Join thousands of music lovers and artists building the future of digital music ownership</p>
          </MainFooterContainer>
        )}
        {/* 일반 페이지용 푸터 */}
        <FooterContent>
          <Logo style={{ color: theme.colors.fontWhite }}>GRAB MUSIC</Logo>
          <SubTitle style={{ marginBottom: '40px', color: theme.colors.fontWhite }}>
            Revolutionizing music ownership through blockchain technology
          </SubTitle>

          <Links>
            <LinkButton onClick={() => handleTermClick(privacyTerm)}>Privacy</LinkButton>
            <Separator>|</Separator>
            <LinkButton onClick={() => handleTermClick(serviceTerm)}>Terms</LinkButton>
            <Separator>|</Separator>
            <Link href="mailto:contact@pondmakers.com">Contact</Link>
          </Links>

          <CompanyInfo>
            Pondmakers Inc. | 11F Jaram Building, 78 Mapo-daero, Mapo-gu, Seoul | CEO: Jireh Park | Business
            Registration No.: 580-87-02446
          </CompanyInfo>

          <Copyright>Copyright © 2025 Pondmakers Inc.</Copyright>
        </FooterContent>
      </FooterContainer>
    </>
  );
};

const FooterContainer = styled.footer<{
  $isMain: boolean;
  $bgUrl?: string;
  $isLoaded?: boolean;
  $playerPB?: number;
}>`
  width: 100%;
  min-height: ${({ $isMain }) => ($isMain ? '800px' : '378px')}; // 메인 페이지는 더 큰 높이
  background: ${({ $isMain, $bgUrl }) =>
    $isMain && $bgUrl
      ? `url(${$bgUrl})`
      : !$isMain
        ? 'linear-gradient(135deg, #72B7EC 0%, #A299F4 100%)'
        : 'linear-gradient(135deg, #72B7EC 0%, #A299F4 100%)'};
  background-size: cover;
  background-position: center top; // 상단부터 표시되도록 변경
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ $isMain }) => ($isMain ? 'flex-end' : 'center')}; // 메인 페이지는 하단 정렬
  position: relative;
  margin-bottom: ${({ $playerPB }) => $playerPB}px;
  padding: ${({ $isMain }) => ($isMain ? '40px 20px 60px' : '40px 20px')};
  opacity: ${({ $isLoaded, $isMain }) => (!$isMain || $isLoaded ? 1 : 0.95)};

  @media (max-width: ${({ theme }) => theme.breakpoints.wide}) {
    min-height: ${({ $isMain }) => ($isMain ? '700px' : '350px')};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    min-height: ${({ $isMain }) => ($isMain ? '600px' : '320px')};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: ${({ $isMain }) => ($isMain ? '500px' : '300px')};
    padding: ${({ $isMain }) => ($isMain ? '20px 20px 40px' : '30px 20px')};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: ${({ $isMain }) => ($isMain ? '400px' : '280px')};
    padding: ${({ $isMain }) => ($isMain ? '20px 20px 30px' : '20px 20px')};
  }
`;

const MainFooterContainer = styled.div<{ $isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #ffffff;
  padding: 0 20px;
  max-width: 1200px;
  width: 100%;
  margin-top: auto; // 자동으로 상단 여백 확보
  margin-bottom: 100px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.5s ease;

  h1 {
    font-size: 68px;
    font-weight: 800;
    margin-bottom: 16px;
    line-height: normal;
    font-family: ${({ theme }) => theme.fonts.pretendard};

    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      font-size: 56px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      font-size: 44px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      font-size: 32px;
    }
  }

  p {
    width: 100%;
    font-size: 24px;
    font-weight: 400;
    margin-bottom: 40px;
    line-height: normal;
    font-family: ${({ theme }) => theme.fonts.pretendard};
    text-align: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
      font-size: 20px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      font-size: 18px;
      margin-bottom: 30px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      font-size: 16px;
      margin-bottom: 20px;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    text-align: center;
    margin-top: 100px;
    margin-bottom: 40px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-top: 80px;
    margin-bottom: 20px;
    text-align: center;
  }
`;

const FooterContent = styled.div`
  text-align: center;
  color: #ffffff;
  padding: 0 20px;
  width: 100%;
  max-width: 1200px;

  & > p,
  h1 {
    margin: 0 auto;
  }
`;

const LoadingPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #72b7ec 0%, #a299f4 100%);
  z-index: -1;
`;

const Logo = styled(SectionTitle)`
  font-size: 36px;
  margin-bottom: 16px;
`;

const Links = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 32px;
`;

// 기본 링크 스타일
const Link = styled.a`
  color: #ffffff;
  text-decoration: none;
  font-family: ${({ theme }) => theme.fonts.pretendard};
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

// 버튼처럼 작동하는 링크 (Link 스타일 상속)
const LinkButton = styled.button`
  /* 버튼 기본 스타일 제거 */
  background: none;
  border: none;
  padding: 0;
  margin: 0;

  /* Link와 동일한 스타일 적용 */
  color: #ffffff;
  text-decoration: none;
  font-family: ${({ theme }) => theme.fonts.pretendard};
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }

  &:focus {
    outline: none;
  }
`;

const Separator = styled.span`
  color: #ffffff;
`;

const CompanyInfo = styled.p`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  opacity: 0.8;
  margin-bottom: 16px;
  font-family: ${({ theme }) => theme.fonts.pretendard};

  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 1.8;
  }
`;

const Copyright = styled.p`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  font-family: ${({ theme }) => theme.fonts.pretendard};
`;
